import { MapContainer, GeoJSON, Circle, Tooltip, Polygon, CircleMarker, useMapEvents, FeatureGroup, useMap } from "react-leaflet";
import chroma from "chroma-js";
import L from "leaflet";
import "leaflet-draw";
import React, { useRef, useEffect, useState, useCallback } from "react";
import "leaflet-draw/dist/leaflet.draw.css";
import AnimatedTripsView from "./AnimatedTripsView";
import NuvolaPunti from "./NuvolaPunti";
import { Tooltip as AntTooltip, Card, Spin } from "antd";
import { IoEarth } from "react-icons/io5";
import { BiVideoRecording } from "react-icons/bi";
import { MdClear, MdClose } from "react-icons/md";
import { PiClipboardText } from "react-icons/pi";
import { FaRoad } from "react-icons/fa";
import { TraffixTileLayer, ThemeToggleButton } from "../../common/map";

// Map components
import { ModuleSlider } from '../../components/map';
import MapControls from '../../components/map/MapControls';


// eslint-disable-next-line no-unused-vars
import { PredictChart } from '../predict';
// eslint-disable-next-line no-unused-vars
import { TlGreenWaveControls } from '../tl-green-wave';

// Constants
import {
  ExchangeIcon,
  AiIcon,
  IconMapa,
  IconNuvola,
  RumorositaIcon,
  FlowIcon,
  TravelTimeIcon,
  RumorositaSider,
  VibrazioneSider
} from '../../constants/icons';
// eslint-disable-next-line no-unused-vars
import { MAP_MODES, NETWORK_CATEGORIES, SIDER_SECTIONS, TIMEOUTS } from '../../constants/mapConstants';

// Services and data
import useTrafficData from "../../components/TrafficDataLoader";
import TrafficChartPopup from "../../components/TrafficChartPopup";
import { getClientType } from '../../services/clientConfig';
import { fetchClientJSON } from "../../services/clientDataService";
import { fetchTrafficLights } from '../tl-green-wave/APIs/greenWaveApi';
import { useLoading } from '../../common/loading/LoadingProvider';
import {
  clearSemafixHandoff,
  handoffToSyncfixSeed,
  hydrateSyncfixArea,
  readSemafixHandoff,
  syncfixPolygonBbox,
} from '../semafix/model/syncfixHandoff';

// Styles
import "../../styles/mappa.css";

// TL Green Wave map event handler component
function TlgwMapEventHandler({ isDrawing, isAddingManual, onMapClick, onMapDoubleClick }) {
  useMapEvents({
    click: (e) => {
      if (isDrawing || isAddingManual) {
        onMapClick(e);
      }
    },
    dblclick: (e) => {
      if (isDrawing) {
        L.DomEvent.stopPropagation(e);
        onMapDoubleClick(e);
      }
    }
  });
  return null;
}

// Helper component to set up map reference and draw event listener
function MapSetup({ mapRef, onMapReady, tlgwFeatureGroupRef, tlgwDrawnLayerRef, setTlgwPolygonPoints, setTlgwIsDrawing, tlgwDrawHandlerRef, tlgwIsEditingRef }) {
  const map = useMap();

  useEffect(() => {
    if (!map) return undefined;

    mapRef.current = map;
    onMapReady();

    // Initial size adjustment — guard against unmount / missing panes
    const sizeTimer = setTimeout(() => {
      try {
        if (map.getContainer()?.parentNode && map._mapPane) {
          map.invalidateSize({ animate: false });
        }
      } catch (_) {
        // Map may already be torn down
      }
    }, 100);

    // Set up draw event listener
    const handleCreated = (e) => {
      const layer = e.layer;

      // Add layer to feature group
      if (tlgwFeatureGroupRef.current) {
        // Remove any existing layer first
        if (tlgwDrawnLayerRef.current) {
          tlgwFeatureGroupRef.current.removeLayer(tlgwDrawnLayerRef.current);
        }
        tlgwFeatureGroupRef.current.addLayer(layer);
      }

      tlgwDrawnLayerRef.current = layer;
      tlgwIsEditingRef.current = false;

      // Get polygon coordinates
      const latlngs = layer.getLatLngs()[0];
      const points = latlngs.map(latlng => [latlng.lat, latlng.lng]);
      setTlgwPolygonPoints(points);
      setTlgwIsDrawing(false);
      if (tlgwDrawHandlerRef.current) {
        tlgwDrawHandlerRef.current = null;
      }
    };

    const handleEdited = (e) => {
      const layers = e.layers;
      layers.eachLayer((layer) => {
        if (layer === tlgwDrawnLayerRef.current) {
          const latlngs = layer.getLatLngs()[0];
          const points = latlngs.map(latlng => [latlng.lat, latlng.lng]);
          setTlgwPolygonPoints(points);
        }
      });
    };

    map.on(L.Draw.Event.CREATED, handleCreated);
    map.on(L.Draw.Event.EDITED, handleEdited);

    return () => {
      clearTimeout(sizeTimer);
      try {
        if (typeof map.stop === 'function') map.stop();
      } catch (_) { /* ignore */ }
      map.off(L.Draw.Event.CREATED, handleCreated);
      map.off(L.Draw.Event.EDITED, handleEdited);
      if (mapRef.current === map) {
        mapRef.current = null;
      }
    };
  }, [map, mapRef, onMapReady, tlgwFeatureGroupRef, tlgwDrawnLayerRef, setTlgwPolygonPoints, setTlgwIsDrawing, tlgwDrawHandlerRef, tlgwIsEditingRef]);

  return null;
}

/** Safely run a Leaflet map operation (avoids _leaflet_pos crashes on torn-down maps).
 *  Pass { stop: false } for lightweight ops (e.g. invalidateSize) so active zoom/pan is not aborted.
 */
function withLiveMap(map, fn, options = {}) {
  if (!map) return false;
  try {
    const container = map.getContainer?.();
    if (!container?.parentNode || !map._mapPane) return false;
    // Only abort in-flight zoom/pan when explicitly requested (default true for setView/fitBounds)
    if (options.stop !== false && typeof map.stop === 'function') map.stop();
    fn(map);
    return true;
  } catch (e) {
    console.warn('[Map] Skipped map operation — map not ready:', e?.message || e);
    return false;
  }
}

function MapComponent({
  geoData,
  onFeatureSelect,
  styleFeature,
  siderSection,
  setSiderSection,
  setShowDeckGL,
  mapMode,
  setMapMode,

  selectedHash,
  setSelectedHash,
  setSelectedFeature,
  footerOpen = true,
  footerHeight = 86.8,
  radarSelected,
  setRadarSelected,
  networkViewCategory,
  setNetworkViewCategory,
  networkViewDate,
  networkViewTime,

  onClearAIFilter,
  aiFilteredRoads,
  onMapCentered,
  mcCityData,
  hasMysteryClient,
  hasOnlyMysteryClient,
  shouldCenterOnMcCity,
}) {
  const {
    showLoader,
    hideLoader,
    beginOperation,
    endOperation,
  } = useLoading();
  const mapRef = useRef();
  const geoJsonLayerRef = useRef(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [showNavbarControls, setShowNavbarControls] = useState(false);
  const [mapTheme, setMapTheme] = useState('dark'); // 'dark' or 'light'
  const [showSimfixAreas, setShowSimfixAreas] = useState(false);
  const [showPredictMode, setShowPredictMode] = useState(false);
  const [showTlGreenWave, setShowTlGreenWave] = useState(false);

  // ── Mystery Client centring ────────────────────────────────────────────────
  // The map only centres on the client's city; the zone and its label belong to
  // the Mystery Client dashboard, which the module button opens directly.
  const mcZoneCenteredRef = useRef(false);
  // FCD network fitBounds must run only once — re-fitting on every parent re-render
  // (e.g. road select) was resetting zoom back to the full network overview.
  const fcdBoundsFittedRef = useRef(false);

  // Check for module activation from redirect (from other pages via ModuleSlider)
  useEffect(() => {
    const moduleToActivate = sessionStorage.getItem('activateModule');
    if (moduleToActivate) {
      // Clear the sessionStorage immediately
      sessionStorage.removeItem('activateModule');

      // Activate the appropriate module
      switch (moduleToActivate) {
        case 'fcd':
          setShowNavbarControls(true);
          setShowSimfixAreas(false);
          setShowPredictMode(false);
          setShowTlGreenWave(false);
          if (setNetworkViewCategory) setNetworkViewCategory('velocita');
          break;
        case 'simfix':
          setShowNavbarControls(false);
          setShowSimfixAreas(true);
          setShowPredictMode(false);
          setShowTlGreenWave(false);
          break;
        case 'predict':
          setShowNavbarControls(false);
          setShowSimfixAreas(false);
          setShowPredictMode(true);
          setShowTlGreenWave(false);
          break;
        case 'tlgreenwave':
          setShowNavbarControls(false);
          setShowSimfixAreas(false);
          setShowPredictMode(false);
          setShowTlGreenWave(true);
          break;
        default:
          break;
      }
    }
  }, [setNetworkViewCategory]);

  // Traffic data state
  // eslint-disable-next-line no-unused-vars
  const { stradeData, varchiData, loading: trafficLoading, error: trafficError } = useTrafficData();
  const [showTrafficChart, setShowTrafficChart] = useState(false);
  const [trafficChartData, setTrafficChartData] = useState(null);

  // Simfix area data (loaded from client-specific JSON)
  const [simfixAreaData, setSimfixAreaData] = useState(null);
  const [simfixLoading, setSimfixLoading] = useState(false);

  // Sync simfixLoading with global loader
  useEffect(() => {
    if (simfixLoading) {
      showLoader("Caricamento SIMFIX Areas...");
    } else {
      hideLoader();
    }
  }, [simfixLoading, showLoader, hideLoader]);

  // Predict mode state
  // Predict mode state
  const [predictSelectedStreets, setPredictSelectedStreets] = useState([]);
  const [showPredictCard, setShowPredictCard] = useState(false);

  // Keep hover tooltips in sync without remounting GeoJSON. Selected streets
  // must not leave a permanent label box on top of the road.
  useEffect(() => {
    const layerGroup = geoJsonLayerRef.current;
    if (!layerGroup || typeof layerGroup.eachLayer !== 'function') return;

    layerGroup.eachLayer((layer) => {
      const feature = layer.feature;
      if (!feature?.properties) return;
      const hash = feature.properties.hash || feature.properties.UnifiedID;
      const isSelected = selectedHash && selectedHash.includes(hash);

      if (layer.getTooltip && layer.getTooltip()) {
        layer.unbindTooltip();
      }
      const streetName = feature.properties.StreetName || 'N/A';
      layer.bindTooltip(
        `<div style="text-align: center;"><strong>${streetName}</strong></div>`,
        {
          permanent: false,
          direction: 'center',
          className: `street-tooltip ${isSelected ? 'selected' : ''}`,
          opacity: 0.9,
        }
      );
      if (layer.closeTooltip) {
        layer.closeTooltip();
      }
    });
  }, [selectedHash, aiFilteredRoads, showPredictMode, predictSelectedStreets]);

  // TL Green Wave state
  const [tlgwIsDrawing, setTlgwIsDrawing] = useState(false);
  const [tlgwPolygonPoints, setTlgwPolygonPoints] = useState([]);
  const [tlgwTrafficLights, setTlgwTrafficLights] = useState([]);
  const [tlgwIsAddingManual, setTlgwIsAddingManual] = useState(false);
  const [tlgwIsLoadingCarica, setTlgwIsLoadingCarica] = useState(false);
  const [tlgwIsSelecting, setTlgwIsSelecting] = useState(false);
  const [tlgwSelectedLights, setTlgwSelectedLights] = useState([]);
  const [tlgwAnimationStates, setTlgwAnimationStates] = useState(null); // { lightId: 'green' | 'red' }
  const [tlgwPersistedSemaforiConfig, setTlgwPersistedSemaforiConfig] = useState(null);
  const [tlgwPersistedLinksConfig, setTlgwPersistedLinksConfig] = useState(null);
  const [tlgwPersistedVelocity, setTlgwPersistedVelocity] = useState(50);
  const [tlgwOpenConfigModalRequest, setTlgwOpenConfigModalRequest] = useState(null);
  const [tlgwIsEditing, setTlgwIsEditing] = useState(false);
  const tlgwIsEditingRef = useRef(false);
  const [mapReady, setMapReady] = useState(false);
  const tlgwFeatureGroupRef = useRef(null);
  const tlgwDrawnLayerRef = useRef(null);
  const semafixHandoffConsumedRef = useRef(false);

  useEffect(() => {
    if (!showTlGreenWave || !mapReady || semafixHandoffConsumedRef.current) return undefined;
    const handoff = readSemafixHandoff();
    const seed = handoffToSyncfixSeed(handoff);
    if (!handoff || !seed) return undefined;

    semafixHandoffConsumedRef.current = true;
    const {
      lights,
      configs,
      links,
      velocity,
      polygonPoints,
      openConfigModal,
    } = seed;
    setTlgwTrafficLights(lights);
    setTlgwSelectedLights(lights.map((light) => light.id));
    setTlgwIsSelecting(true);
    setTlgwPersistedSemaforiConfig(configs.map((config) => ({ ...config, client_type: getClientType() })));
    setTlgwPersistedLinksConfig(links);
    setTlgwPersistedVelocity(velocity);

    let cancelled = false;
    let loadingId = null;
    let bounds;
    if (polygonPoints.length >= 3) {
      setTlgwPolygonPoints(polygonPoints);
      if (tlgwFeatureGroupRef.current) {
        tlgwFeatureGroupRef.current.clearLayers();
        const polygonLayer = L.polygon(polygonPoints, {
          color: '#8C74FF',
          weight: 2,
          fillColor: '#8C74FF',
          fillOpacity: 0.2,
        });
        tlgwFeatureGroupRef.current.addLayer(polygonLayer);
        tlgwDrawnLayerRef.current = polygonLayer;
      }
      bounds = L.latLngBounds(polygonPoints);
      mapRef.current?.fitBounds(bounds, { padding: [60, 60] });
    } else {
      bounds = L.latLngBounds(lights.map((light) => [light.lat, light.lon]));
      if (lights.length > 1) mapRef.current?.fitBounds(bounds, { padding: [80, 80] });
      else mapRef.current?.flyTo([lights[0].lat, lights[0].lon], 16);
    }

    const polygonBbox = syncfixPolygonBbox(polygonPoints);
    const radius = 0.015;
    const center = bounds.getCenter();
    const bbox = polygonBbox
      || `${center.lng - radius},${center.lat - radius},${center.lng + radius},${center.lat + radius}`;
    if (polygonBbox) {
      loadingId = `semafix-syncfix-${handoff.createdAt || Date.now()}`;
      beginOperation(
        loadingId,
        'Trasferimento area e semafori da SEMAFIX…',
        { showPercentage: false },
      );
    }

    fetchTrafficLights(bbox).then((data) => {
      if (cancelled) return;
      const nearby = Array.isArray(data?.traffic_lights) ? data.traffic_lights : [];
      if (nearby.length === 0) return;

      if (polygonBbox) {
        const hydrated = hydrateSyncfixArea(handoff, nearby);
        if (!hydrated) return;
        setTlgwTrafficLights(hydrated.allLights);
        setTlgwSelectedLights(hydrated.selectedLights.map((light) => light.id));
        setTlgwIsSelecting(false);
        setTlgwPersistedSemaforiConfig(hydrated.configs.map((config) => ({
          ...config,
          client_type: getClientType(),
        })));
        setTlgwPersistedLinksConfig(hydrated.links);
        setTlgwPersistedVelocity(hydrated.velocity);
        if (openConfigModal && hydrated.selectedLights.length >= 2) {
          setTlgwOpenConfigModalRequest(`semafix-${handoff.createdAt || Date.now()}`);
        }
        return;
      }

      // Snap each SEMAFIX intersection onto the closest real traffic light, when there is one.
      const taken = new Set();
      const resolved = lights.map((light) => {
        const nearest = nearby.reduce((best, candidate) => {
          if (taken.has(String(candidate.id))) return best;
          const distance = Math.hypot(Number(candidate.lat) - light.lat, Number(candidate.lon) - light.lon);
          return !best || distance < best.distance ? { candidate, distance } : best;
        }, null);
        if (!nearest || nearest.distance >= 0.0008) return light;
        taken.add(String(nearest.candidate.id));
        return { ...nearest.candidate, semafixIntersectionId: light.semafixIntersectionId };
      });

      const resolvedIds = new Set(resolved.map((light) => String(light.id)));
      setTlgwTrafficLights([
        ...resolved,
        ...nearby.filter((candidate) => !resolvedIds.has(String(candidate.id))),
      ]);
      setTlgwSelectedLights(resolved.map((light) => light.id));
      setTlgwPersistedSemaforiConfig(resolved.map((light, index) => ({
        ...configs[index],
        id: light.id,
        coordinates: [Number(light.lat), Number(light.lon ?? light.lng)],
        client_type: getClientType(),
      })));
      setTlgwPersistedLinksConfig(links.map((link, index) => ({
        ...link,
        fromId: resolved[index].id,
        toId: resolved[index + 1].id,
      })));
      if (openConfigModal && resolved.length >= 2) {
        setTlgwOpenConfigModalRequest(`semafix-${handoff.createdAt || Date.now()}`);
      }
    }).catch(() => {
      // The seeded SEMAFIX lights remain usable when the traffic-light service is unavailable.
    }).finally(() => {
      if (loadingId) endOperation(loadingId);
      clearSemafixHandoff();
    });

    return () => {
      cancelled = true;
      if (loadingId) endOperation(loadingId);
    };
  }, [beginOperation, endOperation, mapReady, showTlGreenWave]);

  // Fix default marker icons path for Leaflet when adding geolocation marker
  // This is safe to call multiple times
  // eslint-disable-next-line no-underscore-dangle
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
    iconUrl: require("leaflet/dist/images/marker-icon.png"),
    shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
  });

  // Initialize map size
  useEffect(() => {
    const timer = setTimeout(() => {
      withLiveMap(mapRef.current, (map) => map.invalidateSize({ animate: false }), { stop: false });
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  // Recalculate map size whenever the footer visibility/height changes
  useEffect(() => {
    const timer = setTimeout(() => {
      withLiveMap(mapRef.current, (map) => map.invalidateSize({ animate: false }), { stop: false });
    }, 100);
    return () => clearTimeout(timer);
  }, [footerOpen, footerHeight]);

  // Auto-fit map on initial load ONLY (never again on selection / parent re-renders):
  // - Without FCD NETX + with Mystery Client → snap to client's city (no animation; loader covers until done)
  // - With FCD NETX → FCD network GeoJSON bounds (mystery-client ignored)
  useEffect(() => {
    // Prefer Mystery Client city when user has MC but not FCD NETX (empty map, no overlays)
    if (shouldCenterOnMcCity) {
      if (mcCityData?.center && mapReady && !mcZoneCenteredRef.current) {
        const ok = withLiveMap(mapRef.current, (map) => {
          // Instant snap — loader stays up so the user never sees a city-switch animation
          map.setView(mcCityData.center, 13, { animate: false });
        });
        if (ok) {
          mcZoneCenteredRef.current = true;
          if (onMapCentered) onMapCentered(mapRef.current);
        }
      }
      return;
    }

    if (fcdBoundsFittedRef.current) return;
    if (!mapReady || !geoData?.features?.length) return;

    try {
      // Calculate bounds from all GeoJSON features
      const allCoords = [];
      geoData.features.forEach(feature => {
        if (feature.geometry && feature.geometry.coordinates) {
          const coords = feature.geometry.coordinates;
          if (feature.geometry.type === 'LineString') {
            coords.forEach(coord => allCoords.push([coord[1], coord[0]])); // [lat, lng]
          } else if (feature.geometry.type === 'MultiLineString') {
            coords.forEach(line => line.forEach(coord => allCoords.push([coord[1], coord[0]])));
          } else if (feature.geometry.type === 'Point') {
            allCoords.push([coords[1], coords[0]]);
          }
        }
      });

      if (allCoords.length > 0) {
        const bounds = L.latLngBounds(allCoords);
        if (bounds.isValid()) {
          const ok = withLiveMap(mapRef.current, (map) => {
            // No animation — avoids _leaflet_pos crash when loader/layout changes mid-transition
            map.fitBounds(bounds, {
              padding: [30, 30],
              maxZoom: 15,
              animate: false,
            });
            map._boundsFitted = true;
          });
          if (ok) {
            fcdBoundsFittedRef.current = true;
            if (onMapCentered) onMapCentered(mapRef.current);
          }
        }
      }
    } catch (e) {
      console.log('Could not auto-fit to GeoJSON bounds:', e);
    }
  }, [geoData, mcCityData, shouldCenterOnMcCity, onMapCentered, mapReady]);

  // Additional resize handler for window resize events
  useEffect(() => {
    const handleResize = () => {
      withLiveMap(mapRef.current, (map) => map.invalidateSize({ animate: false }), { stop: false });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest(".mode-dropdown")) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [showDropdown]);

  // Listen for showTrafficChart event from TrafficMarkers component
  useEffect(() => {
    const handleShowTrafficChart = (event) => {
      const { type, id } = event.detail;
      setTrafficChartData({ type, id });
      setShowTrafficChart(true);
    };

    window.addEventListener('showTrafficChart', handleShowTrafficChart);
    return () => window.removeEventListener('showTrafficChart', handleShowTrafficChart);
  }, []);

  // Load simfix area data when SIMFIX Areas is activated
  useEffect(() => {
    if (showSimfixAreas && !simfixAreaData) {
      setSimfixLoading(true);
      fetchClientJSON(getClientType(), 'simfix_area.json')
        .then(data => {
          setSimfixAreaData(data);
          setSimfixLoading(false);
        })
        .catch(err => {
          console.error('Error loading simfix area:', err);
          setSimfixLoading(false);
        });
    }
  }, [showSimfixAreas, simfixAreaData]);

  // Reset predict mode state when mode is deactivated
  useEffect(() => {
    if (!showPredictMode) {
      setPredictSelectedStreets([]);
      setShowPredictCard(false);
    }
  }, [showPredictMode]);

  // DISABLED: Reset TL Green Wave state when module is deactivated
  // We want to keep selection and settings in memory as requested by the user
  /*
  useEffect(() => {
    if (!showTlGreenWave) {
      setTlgwIsDrawing(false);
      setTlgwPolygonPoints([]);
      setTlgwTrafficLights([]);
      setTlgwIsAddingManual(false);
      setTlgwIsLoadingCarica(false);
      setTlgwIsSelecting(false);
      setTlgwSelectedLights([]);
    }
  }, [showTlGreenWave]);
  */

  // Load traffic lights from API based on polygon bbox, then filter by polygon containment
  const handleLoadTrafficLights = async () => {
    if (tlgwPolygonPoints.length < 3) return;

    // Calculate bbox from polygon points
    const lats = tlgwPolygonPoints.map(p => p[0]);
    const lngs = tlgwPolygonPoints.map(p => p[1]);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);

    const bbox = `${minLng},${minLat},${maxLng},${maxLat}`;

    setTlgwIsLoadingCarica(true);
    try {
      const data = await fetchTrafficLights(bbox);

      if (data.traffic_lights && data.traffic_lights.length > 0) {
        // Filter: only keep lights inside the drawn polygon (point-in-polygon test)
        const filteredLights = data.traffic_lights.filter(light => {
          return isPointInPolygon([light.lat, light.lon], tlgwPolygonPoints);
        });
        setTlgwTrafficLights(filteredLights);
        // Auto-enable selection mode once traffic lights are loaded
        if (filteredLights.length > 0) {
          setTlgwIsSelecting(true);
        }
      } else {
        setTlgwTrafficLights([]);
      }
    } catch (error) {
      console.error('Error loading traffic lights:', error);
      setTlgwTrafficLights([]);
    } finally {
      setTlgwIsLoadingCarica(false);
    }
  };

  // Ray-casting point-in-polygon test
  function isPointInPolygon(point, polygon) {
    const [y, x] = point; // [lat, lng]
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const [yi, xi] = polygon[i];
      const [yj, xj] = polygon[j];
      const intersect = ((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  }

  // Clear all TL Green Wave data
  const handleClearTlgw = () => {
    setTlgwPolygonPoints([]);
    setTlgwTrafficLights([]);
    setTlgwIsDrawing(false);
    setTlgwIsAddingManual(false);
    setTlgwIsSelecting(false);
    setTlgwSelectedLights([]);
    setTlgwOpenConfigModalRequest(null);
    // Clear Leaflet Draw layers
    if (tlgwFeatureGroupRef.current) {
      tlgwFeatureGroupRef.current.clearLayers();
    }
    tlgwDrawnLayerRef.current = null;
  };

  // Handle map click for manual point addition only
  const handleTlgwMapClick = (e) => {
    const { lat, lng } = e.latlng;

    if (tlgwIsAddingManual) {
      // Add manual traffic light
      const manualId = `manual-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      setTlgwTrafficLights(prev => [
        ...prev,
        {
          id: manualId,
          lat: lat,
          lon: lng,
          tags: { highway: 'traffic_signals', manual: true }
        }
      ]);
      setTlgwIsAddingManual(false);
    }
  };

  // Ref to store the active draw handler
  const tlgwDrawHandlerRef = useRef(null);

  // Function to programmatically start polygon drawing
  const handleStartPolygonDraw = useCallback(() => {
    if (!mapReady || !mapRef.current) {
      console.warn('Map not ready for drawing');
      return;
    }

    // Disable any existing draw handler
    if (tlgwDrawHandlerRef.current) {
      tlgwDrawHandlerRef.current.disable();
    }

    // Clear existing polygon if any
    if (tlgwFeatureGroupRef.current && tlgwDrawnLayerRef.current) {
      tlgwFeatureGroupRef.current.removeLayer(tlgwDrawnLayerRef.current);
      tlgwDrawnLayerRef.current = null;
    }
    setTlgwPolygonPoints([]);

    // Create and enable new polygon draw handler
    const drawHandler = new L.Draw.Polygon(mapRef.current, {
      allowIntersection: false,
      showArea: true,
      shapeOptions: {
        color: '#8C74FF',
        weight: 2,
        fillColor: '#8C74FF',
        fillOpacity: 0.2
      }
    });

    tlgwDrawHandlerRef.current = drawHandler;
    drawHandler.enable();
    setTlgwIsDrawing(true);
  }, [mapReady]);

  // Function to programmatically start polygon editing
  const handleStartPolygonEdit = useCallback(() => {
    if (!mapReady || !mapRef.current || !tlgwDrawnLayerRef.current) {
      console.warn('Map not ready or no polygon to edit');
      return;
    }

    if (tlgwIsEditingRef.current) {
      // Save/Stop editing
      if (tlgwDrawHandlerRef.current) {
        tlgwDrawHandlerRef.current.save();
        tlgwDrawHandlerRef.current.disable();
        tlgwDrawHandlerRef.current = null;
      }
      setTlgwIsEditing(false);
      tlgwIsEditingRef.current = false;
    } else {
      // Start editing
      const editHandler = new L.EditToolbar.Edit(mapRef.current, {
        featureGroup: tlgwFeatureGroupRef.current,
        selectedPathOptions: {
          color: '#8C74FF',
          weight: 4,
          dashArray: '10, 10'
        }
      });

      tlgwDrawHandlerRef.current = editHandler;
      editHandler.enable();
      setTlgwIsEditing(true);
      tlgwIsEditingRef.current = true;
    }
  }, [mapReady]);

  // Callback for when map is ready (called by MapSetup component)
  const handleMapReady = useCallback(() => {
    setMapReady(true);
  }, []);

  // Handle traffic light click for selection with direction validation
  const handleTlgwLightClick = (lightId) => {
    if (!tlgwIsSelecting) return;

    setTlgwSelectedLights(prev => {
      const existingIndex = prev.indexOf(lightId);
      if (existingIndex !== -1) {
        // Remove if already selected
        return prev.filter(id => id !== lightId);
      } else {
        // Direction validation: check the new light doesn't create a zigzag
        if (prev.length >= 2) {
          const getCoords = (id) => {
            const l = tlgwTrafficLights.find(tl => tl.id === id);
            return l ? [l.lat, l.lon] : null;
          };

          const coords = prev.map(getCoords).filter(Boolean);
          const newCoord = getCoords(lightId);

          if (newCoord && coords.length >= 2) {
            // Calculate direction between first two selected lights
            const dLat0 = coords[1][0] - coords[0][0];
            const dLng0 = coords[1][1] - coords[0][1];

            // Calculate direction from last selected to new
            const lastCoord = coords[coords.length - 1];
            const dLat1 = newCoord[0] - lastCoord[0];
            const dLng1 = newCoord[1] - lastCoord[1];

            // Check dot product – negative means reversed direction (zigzag)
            const dot = dLat0 * dLat1 + dLng0 * dLng1;
            if (dot < 0) {
              // Zigzag detected – reject
              return prev;
            }
          }
        }

        return [...prev, lightId];
      }
    });
  };

  // Remove a traffic light from selection by ID
  const handleRemoveTlgwLight = (lightId) => {
    setTlgwSelectedLights(prev => prev.filter(id => id !== lightId));
  };

  // Clear all selected traffic lights
  const handleClearTlgwSelection = () => {
    setTlgwSelectedLights([]);
  };

  // Handle traffic light animation state changes from SimulazioneModal
  const handleTlgwAnimationStateChange = (stateMap) => {
    setTlgwAnimationStates(stateMap);
  };

  // Expose function to select roads from network when clicking traffic polylines
  useEffect(() => {
    window.selectRoadFromNetwork = (streetName) => {
      if (!geoData) return;

      console.log('Selecting road from network:', streetName);

      // Find the road feature that matches this street name (case-insensitive, exact match preferred)
      const cleanStreetName = streetName.trim().toLowerCase();

      // First try exact match
      let matchingFeature = geoData.features.find(f => {
        const featureStreetName = (f.properties.StreetName || '').trim().toLowerCase();
        return featureStreetName === cleanStreetName;
      });

      // If no exact match, try partial match (but only if street names share significant overlap)
      if (!matchingFeature) {
        matchingFeature = geoData.features.find(f => {
          const featureStreetName = (f.properties.StreetName || '').trim().toLowerCase();
          // Only match if one contains the other AND they share at least 80% of the shorter name
          if (featureStreetName.includes(cleanStreetName) || cleanStreetName.includes(featureStreetName)) {
            const shorter = cleanStreetName.length < featureStreetName.length ? cleanStreetName : featureStreetName;
            const longer = cleanStreetName.length >= featureStreetName.length ? cleanStreetName : featureStreetName;
            // Calculate overlap ratio
            return shorter.length / longer.length >= 0.8;
          }
          return false;
        });
      }

      if (matchingFeature && onFeatureSelect) {
        const hash = matchingFeature.properties.hash || matchingFeature.properties.UnifiedID;
        console.log('Found matching feature:', matchingFeature.properties.StreetName, hash);
        onFeatureSelect(hash, matchingFeature.properties);
      } else {
        console.log('No matching feature found for:', streetName);
      }
    };

    return () => {
      delete window.selectRoadFromNetwork;
    };
  }, [geoData, onFeatureSelect]);

  const handleModeChange = (mode) => {
    setIsSwitching(true);
    setMapMode(mode);
    setShowDropdown(false);

    setShowDeckGL(false);

    // brief spinner while switching view
    setTimeout(() => setIsSwitching(false), 400);
  };

  const handleDeselectAll = () => {
    // Clear all selected roads
    if (setSelectedHash) {
      setSelectedHash([]);
    }
    // Clear selected features
    if (setSelectedFeature) {
      setSelectedFeature([]);
    }
    // Deselect radar
    if (setRadarSelected) {
      setRadarSelected(false);
    }
    // Clear AI filter to show all roads again
    if (onClearAIFilter) {
      onClearAIFilter();
    }
    // Close sider panel
    setSiderSection(null);
  };

  const onEachFeature = (feature, layer) => {
    // Use UnifiedID or hash - compatibility with new GeoJSON format
    const hash = feature.properties.hash || feature.properties.UnifiedID;
    const isSelected = selectedHash && selectedHash.includes(hash);

    // Get value based on network view category
    const allRecords = feature.properties.allRecords || [];
    let rec;

    if (
      networkViewCategory &&
      networkViewDate &&
      networkViewTime &&
      (!selectedHash || selectedHash.length === 0)
    ) {
      // Find record matching both date and time
      rec = allRecords.find((d) => {
        if (d.day_group_label) {
          const dayGroupParts = d.day_group_label.split('_')[0];

          // Check if it's new format (YYYY-MM-DD)
          const newFormatMatch = dayGroupParts.match(/^(\d{4})-(\d{2})-(\d{2})$/);
          if (newFormatMatch) {
            // New format: direct comparison
            if (dayGroupParts === networkViewDate) {
              const recordTime = (d.t_start || '00:00') + ':00';
              if (recordTime === networkViewTime) {
                return true;
              }
            }
          } else {
            // Old format: parse DD,DD,DD-MM-YYYY
            const parts = dayGroupParts.split('-');
            if (parts.length === 3) {
              const daysStr = parts[0];
              const month = parts[1].padStart(2, '0');
              const year = parts[2];

              const days = daysStr.split(',');
              for (const day of days) {
                if (day && day.trim()) {
                  const dayPadded = day.trim().padStart(2, '0');
                  const recordDate = `${year}-${month}-${dayPadded}`;
                  if (recordDate === networkViewDate) {
                    // Also check time match
                    const recordTime = (d.t_start || '00:00') + ':00';
                    if (recordTime === networkViewTime) {
                      return true;
                    }
                  }
                }
              }
            }
          }
        }
        return false;
      }) || allRecords[0] || {};
    } else {
      rec = allRecords[0] || {};
    }

    // Determine which value to display
    let displayValue = "N/A";
    let displayLabel = "Speed";

    if (networkViewCategory === "velocita") {
      displayLabel = "Velocità";
      const speed = parseFloat(rec.AvgSp) || parseFloat(rec.speed) || 0;
      displayValue = speed > 0 ? speed.toFixed(2) + " km/h" : "N/A";
    } else if (networkViewCategory === "flow") {
      displayLabel = "Flusso";
      const flow = parseFloat(rec.Flusso_Espanso) || parseFloat(rec.flow) || 0;
      displayValue = flow >= 0 ? flow.toFixed(2) + " VEH" : "N/A";
    } else if (networkViewCategory === "traveltime") {
      displayLabel = "Tempo di percorrenza";
      const travelTime = parseFloat(rec.AvgTt) || parseFloat(rec.travel_time_in_seconds) || 0;
      displayValue = travelTime > 0 ? travelTime.toFixed(2) + " s" : "N/A";
    } else {
      // Default to speed
      const speed = parseFloat(rec.AvgSp) || parseFloat(rec.speed) || 0;
      displayValue = speed > 0 ? speed.toFixed(2) + " km/h" : "N/A";
    }

    // Bind tooltip to the layer
    const streetName = feature.properties.StreetName || "N/A";
    const tooltipContent = `
      <div style="text-align: center;">
        <strong>${streetName}</strong>
      </div>
    `;

    layer.bindTooltip(tooltipContent, {
      permanent: false,
      direction: "center",
      className: `street-tooltip ${isSelected ? 'selected' : ''}`,
      opacity: 0.9,
    });

    layer.closeTooltip();

    layer.on("click", () => {
      // When Predict mode is active
      if (showPredictMode) {
        setPredictSelectedStreets(prev => {
          const exists = prev.find(s => s.hash === hash);
          if (exists) {
            // Remove if already selected (deselect)
            return [];
          } else {
            // Replace selection with new street (limit to 1)
            return [{ hash, properties: feature.properties }];
          }
        });
        return;
      }

      // Normal selection for other modules (FCD Network, etc.)
      if (onFeatureSelect) {
        onFeatureSelect(hash, feature.properties);
      }
    });
  };

  return (
    <div
      style={{
        position: "relative",
        height: "100%",
        "--footer-height": footerOpen ? `${footerHeight}px` : "0px",
      }}
    >
      <div className="mode-dropdown" style={{ bottom: "150px", right: "18px" }}>

        {showDropdown && (
          <Card className="mode-dropdown-menu" size="small" bordered={false}>
            <button
              onClick={() => handleModeChange("mappa")}
              className={`mode-option ${mapMode === "mappa" ? "active" : ""}`}
            >
              <div
                className="mode-option-icon"
                style={{ backgroundColor: "#FFE100" }}
              >
                <img src={IconMapa} alt="Mappa GeoJSON" />
              </div>
              <span className="mode-option-text">Vista Generale</span>
            </button>
          </Card>
        )}
      </div>

      <div className="theme-toggle-container" style={{ bottom: "200px", right: "18px" }}>
        <ThemeToggleButton theme={mapTheme} onChange={setMapTheme} />
      </div>

      {/* Module slider - always visible in mappa mode */}
      {mapMode !== "nuvola" && mapMode !== "flusso" && (
        <ModuleSlider
          showNavbarControls={showNavbarControls}
          setShowNavbarControls={setShowNavbarControls}
          showSimfixAreas={showSimfixAreas}
          setShowSimfixAreas={setShowSimfixAreas}
          showPredictMode={showPredictMode}
          setShowPredictMode={setShowPredictMode}
          showTlGreenWave={showTlGreenWave}
          setShowTlGreenWave={setShowTlGreenWave}
          showAssignx={false}
          setShowAssignx={() => { }}
          setNetworkViewCategory={setNetworkViewCategory}
          onModuleChange={() => {
            // Clear FCD Network selections when switching modules
            if (setSelectedHash) setSelectedHash([]);
            if (setSelectedFeature) setSelectedFeature([]);
            if (setRadarSelected) setRadarSelected(false);
            setSiderSection(null);
          }}
        />
      )}

      {mapMode !== "nuvola" && mapMode !== "flusso" && showNavbarControls && (
        <div className="mappa-navbar-controls">
          {/* Determine if we have mixed selection (road + radar) */}
          {(() => {
            const hasRoads = selectedHash && selectedHash.length > 0;
            const hasRadar = radarSelected;

            // Show road buttons by default (no selection) or when at least 1 road is selected (without radar)
            const showRoadButtons = !hasRadar;
            // Show radar buttons only when radar is selected and no roads
            const showRadarButtons = hasRadar && !hasRoads;
            // Show Proprieta by default or when roads are selected (not mixed with radar)
            const showProprietaButton = !hasRadar;

            return (
              <>
                {/* Deselect All button - visible when any road or radar is selected */}
                {(hasRoads || hasRadar) && (
                  <button
                    onClick={handleDeselectAll}
                    disabled={mapMode !== "mappa"}
                    className="sidebar-button sidebar-button-deselect"
                    title="Deseleziona tutto"
                  >
                    <MdClear />
                  </button>
                )}

                {/* Info button - visible when any road or radar is selected */}
                {(hasRoads || hasRadar) && (
                  <button
                    onClick={() => {
                      if (mapMode === "mappa") {
                        setSiderSection(
                          siderSection === "info" ? null : "info"
                        );
                      }
                    }}
                    disabled={mapMode !== "mappa"}
                    className={`sidebar-button ${siderSection === "info" ? "active" : ""
                      }`}
                    title="Informazioni"
                  >
                    <PiClipboardText />
                  </button>
                )}

                {/* Proprieta button - visible only when elements of same type are selected */}
                {showProprietaButton && (
                  <button
                    onClick={() => {
                      if (mapMode === "mappa") {
                        if (hasRoads) {
                          // If roads are selected, show the sider
                          setSiderSection(
                            siderSection === "proprieta" ? null : "proprieta"
                          );
                        } else {
                          // If no roads, toggle network view category
                          if (networkViewCategory === "velocita") {
                            setNetworkViewCategory(null);
                          } else {
                            setNetworkViewCategory("velocita");
                            setSiderSection("velocita");
                          }
                        }
                      }
                    }}
                    disabled={mapMode !== "mappa"}
                    className={`sidebar-button ${(siderSection === "proprieta" && hasRoads) ||
                      (networkViewCategory === "velocita" && !hasRoads)
                      ? "active"
                      : ""
                      }`}
                    title="Velocità"
                  >
                    <img
                      src={RumorositaIcon}
                      alt="Velocità"
                      style={{ width: "20px", height: "20px" }}
                    />
                  </button>
                )}

                {/* Road-specific buttons - shown only when roads are selected (without radar) */}
                {showRoadButtons && (
                  <>
                    <button
                      onClick={() => {
                        if (mapMode === "mappa") {
                          if (hasRoads) {
                            // If roads are selected, show the sider
                            setSiderSection(
                              siderSection === "flow"
                                ? null
                                : "flow"
                            );
                          } else {
                            // If no roads, toggle network view category
                            if (networkViewCategory === "flow") {
                              setNetworkViewCategory(null);
                            } else {
                              setNetworkViewCategory("flow");
                              setSiderSection("flow");
                            }
                          }
                        }
                      }}
                      disabled={mapMode !== "mappa"}
                      className={`sidebar-button ${(siderSection === "flow" && hasRoads) ||
                        (networkViewCategory === "flow" && !hasRoads)
                        ? "active"
                        : ""
                        }`}
                      title="Flusso"
                    >
                      <img
                        src={FlowIcon}
                        alt="Flusso"
                        style={{ width: "20px", height: "20px" }}
                      />
                    </button>

                    <button
                      onClick={() => {
                        if (mapMode === "mappa") {
                          if (hasRoads) {
                            // If roads are selected, show the sider
                            setSiderSection(
                              siderSection === "traveltime" ? null : "traveltime"
                            );
                          } else {
                            // If no roads, toggle network view category
                            if (networkViewCategory === "traveltime") {
                              setNetworkViewCategory(null);
                            } else {
                              setNetworkViewCategory("traveltime");
                              setSiderSection("traveltime");
                            }
                          }
                        }
                      }}
                      disabled={mapMode !== "mappa"}
                      className={`sidebar-button ${(siderSection === "traveltime" && hasRoads) ||
                        (networkViewCategory === "traveltime" && !hasRoads)
                        ? "active"
                        : ""
                        }`}
                      title="Tempo di percorrenza"
                    >
                      <img
                        src={TravelTimeIcon}
                        alt="Tempo di percorrenza"
                        style={{ width: "20px", height: "20px" }}
                      />
                    </button>
                  </>
                )}

                {/* Radar-specific buttons - shown only when radar is selected (without roads) */}
                {showRadarButtons && (
                  <>
                    <button
                      onClick={() => {
                        if (mapMode === "mappa") {
                          setSiderSection(
                            siderSection === "audio" ? null : "audio"
                          );
                        }
                      }}
                      disabled={mapMode !== "mappa"}
                      className={`sidebar-button ${siderSection === "audio" ? "active" : ""
                        }`}
                      title="Rumorosità"
                    >
                      <img
                        src={RumorositaSider}
                        alt="Rumorosità"
                        style={{ width: "20px", height: "20px" }}
                      />
                    </button>

                    <button
                      onClick={() => {
                        if (mapMode === "mappa") {
                          setSiderSection(
                            siderSection === "accelerometrici"
                              ? null
                              : "accelerometrici"
                          );
                        }
                      }}
                      disabled={mapMode !== "mappa"}
                      className={`sidebar-button ${siderSection === "accelerometrici" ? "active" : ""
                        }`}
                      title="Vibrazione"
                    >
                      <img
                        src={VibrazioneSider}
                        alt="Vibrazione"
                        style={{ width: "20px", height: "20px" }}
                      />
                    </button>

                    <button
                      onClick={() => {
                        if (mapMode === "mappa") {
                          setSiderSection(
                            siderSection === "ambientali" ? null : "ambientali"
                          );
                        }
                      }}
                      disabled={mapMode !== "mappa"}
                      className={`sidebar-button ${siderSection === "ambientali" ? "active" : ""
                        }`}
                      title="Media giornaliera ambientale"
                    >
                      <IoEarth />
                    </button>

                    <button
                      onClick={() => {
                        if (mapMode === "mappa") {
                          setSiderSection(
                            siderSection === "video" ? null : "video"
                          );
                        }
                      }}
                      disabled={mapMode !== "mappa"}
                      className={`sidebar-button ${siderSection === "video" ? "active" : ""
                        }`}
                      title="Rilevamento di anomalie video"
                    >
                      <BiVideoRecording />
                    </button>
                  </>
                )}

                {/* Confronto button - always visible and clickable */}
                <button
                  onClick={() => {
                    if (mapMode === "mappa") {
                      if (!hasRoads && !hasRadar) {
                        // Show message when no elements are selected
                        alert(
                          "Nessun elemento selezionato. Seleziona almeno un elemento per visualizzare il confronto."
                        );
                      } else {
                        setSiderSection(
                          siderSection === "confronto" ? null : "confronto"
                        );
                      }
                    }
                  }}
                  disabled={mapMode !== "mappa"}
                  className={`sidebar-button-confronto ${siderSection === "confronto" ? "active" : ""
                    }`}
                  title="Confronto"
                >
                  <img src={ExchangeIcon} alt="" className="confronto-icon" />{" "}
                  Confronto
                </button>

                {/* AI Assistant button */}
                <button
                  onClick={() => {
                    if (mapMode === "mappa") {
                      setSiderSection(
                        siderSection === "ai" ? null : "ai"
                      );
                    }
                  }}
                  disabled={mapMode !== "mappa"}
                  className={`sidebar-button-ai ${siderSection === "ai" ? "active" : ""
                    }`}
                  title="Interpreter"
                >
                  <img src={AiIcon} alt="AI" className="ai-icon" />
                </button>
              </>
            );
          })()}
        </div>
      )}

      {/* Predict Mode Control Buttons - shown ONLY when Predict mode is active */}
      {mapMode !== "nuvola" && mapMode !== "flusso" && showPredictMode && (
        <div className="mappa-navbar-controls predict-navbar">
          {/* Deselect button - shown when streets are selected */}
          {predictSelectedStreets.length > 0 && (
            <button
              className="sidebar-button sidebar-button-deselect"
              onClick={() => setPredictSelectedStreets([])}
              title="Deseleziona tutto"
            >
              <MdClear />
            </button>
          )}
          <div className="predict-btn-wrapper">
            <button
              className={`predict-control-btn predict-run-btn ${showPredictCard ? 'active' : ''}`}
              onClick={() => {
                setShowPredictCard(!showPredictCard);
              }}
              disabled={predictSelectedStreets.length === 0}
            >
              Predict
            </button>


            {/* Predict Results Card - positioned under button */}
            {showPredictCard && predictSelectedStreets.length > 0 && (
              <div className="predict-card">
                <div className="predict-card-header">
                  <div className="predict-card-header-icon">
                    <FaRoad />
                  </div>
                  <span className="predict-card-title">
                    {predictSelectedStreets[0]?.properties?.StreetName || 'Autostrada dei Giovi pos'}
                  </span>
                  <button
                    className="predict-card-close"
                    onClick={() => setShowPredictCard(false)}
                    title="Close"
                  >
                    <MdClose />
                  </button>
                </div>
                <div className="predict-card-content">
                  {/* Prediction Chart */}
                  <PredictChart
                    selectedStreet={predictSelectedStreets[0]}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Removed generic map-loading-overlay spinners here to rely strictly on global LoadingContext */}

      {mapMode === "flusso" ? (
        <>
          <AnimatedTripsView />
        </>
      ) : mapMode === "nuvola" ? (
        <>
          <NuvolaPunti />
        </>
      ) : (!shouldCenterOnMcCity || mcCityData?.center) ? (
        <>
          <MapContainer
            center={shouldCenterOnMcCity && mcCityData?.center
              ? mcCityData.center
              : [44.309405, 9.352045]}
            zoom={shouldCenterOnMcCity ? 13 : 14}
            maxZoom={22}
            zoomControl={false}
            style={{
              height: "100%",
              width: "100%",
            }}
          >
            {/* Legend Overlay for FCD Network — default to speed (roads use speed colors when category is null) */}
            {showNavbarControls && (() => {
              const legendCategory = networkViewCategory || 'velocita';
              return (
              <div className="map-legend-overlay">
                <div className="map-legend-header">
                  <h3 className="map-legend-title">
                    {legendCategory === 'velocita'
                      ? 'Legenda Velocità (km/h)'
                      : legendCategory === 'flow'
                        ? 'Legenda Flusso (VEH)'
                        : 'Legenda Tempo di percorrenza'}
                  </h3>
                </div>
                <div className="legend-gradient-container">
                  <div className={`legend-gradient-bar ${
                    legendCategory === 'velocita'
                      ? 'gradient-velocita'
                      : legendCategory === 'flow'
                        ? 'gradient-flow'
                        : 'gradient-traveltime'
                  }`}></div>
                  <div className="legend-labels">
                    <span className="legend-label">
                      {legendCategory === 'velocita'
                        ? 'Bassa'
                        : legendCategory === 'flow'
                          ? 'Basso'
                          : 'Tempo breve'}
                    </span>
                    <span className="legend-label">
                      {legendCategory === 'velocita'
                        ? 'Alta'
                        : legendCategory === 'flow'
                          ? 'Alto'
                          : 'Tempo lungo'}
                    </span>
                  </div>
                </div>
              </div>
              );
            })()}
            {/* Map Setup - initializes map reference and draw event listener */}
            <MapSetup
              mapRef={mapRef}
              onMapReady={handleMapReady}
              tlgwFeatureGroupRef={tlgwFeatureGroupRef}
              tlgwDrawnLayerRef={tlgwDrawnLayerRef}
              setTlgwPolygonPoints={setTlgwPolygonPoints}
              setTlgwIsDrawing={setTlgwIsDrawing}
              tlgwDrawHandlerRef={tlgwDrawHandlerRef}
              tlgwIsEditingRef={tlgwIsEditingRef}
            />
            <TraffixTileLayer theme={mapTheme} />
            {/* GeoJSON data - only shown when at least one module is active */}
            {geoData && (showNavbarControls || showSimfixAreas || showPredictMode || showTlGreenWave) && (
              <GeoJSON
                /* Stable key: do NOT remount on time/selection — remounting resets view during playback & road select.
                   Style updates via react-leaflet setStyle when the style prop changes. */
                key={`fcd-geo-${networkViewCategory || 'velocita'}-${showPredictMode}-${aiFilteredRoads ? aiFilteredRoads.length : 0}`}
                data={geoData}
                onEachFeature={onEachFeature}
                style={(feature) => {
                  // Get original style from styleFeature prop
                  const originalStyle = styleFeature ? styleFeature(feature) : {};

                  // In Predict mode, make streets gray unless selected
                  if (showPredictMode) {
                    const hash = feature.properties.hash || feature.properties.UnifiedID;
                    const isSelectedForPredict = predictSelectedStreets.some(s => s.hash === hash);

                    if (isSelectedForPredict) {
                      // Show original color for selected streets
                      return originalStyle;
                    } else {
                      // Gray color for unselected streets in Predict mode
                      return {
                        ...originalStyle,
                        color: '#666666',
                        opacity: 0.6
                      };
                    }
                  }

                  return originalStyle;
                }}
                ref={(ref) => {
                  geoJsonLayerRef.current = ref;
                  // Auto-fit map to FCD network bounds on first load only
                  if (
                    shouldCenterOnMcCity ||
                    fcdBoundsFittedRef.current ||
                    !ref ||
                    !mapRef.current ||
                    mapRef.current._boundsFitted ||
                    !geoData.features ||
                    geoData.features.length === 0
                  ) {
                    return;
                  }
                  try {
                    const bounds = ref.getBounds();
                    if (bounds && bounds.isValid()) {
                      withLiveMap(mapRef.current, (map) => {
                        map.fitBounds(bounds, {
                          padding: [30, 30],
                          maxZoom: 15,
                          animate: false,
                        });
                        map._boundsFitted = true;
                        fcdBoundsFittedRef.current = true;
                      });
                    }
                  } catch (e) {
                    console.log('Could not auto-fit bounds:', e);
                  }
                }}
              />
            )}

            {/* SIMFIX Areas - shown when button is active */}
            {showSimfixAreas && (
              <>
                {simfixAreaData && (
                  <Circle
                    center={[simfixAreaData.center[1], simfixAreaData.center[0]]}
                    radius={simfixAreaData.radius || 1500}
                    pathOptions={{
                      fillColor: '#B8860B',
                      fillOpacity: 0.35,
                      color: '#DAA520',
                      weight: 2,
                      opacity: 0.6
                    }}
                    eventHandlers={{
                      click: () => {
                        const clientType = getClientType();
                        const queryParams = clientType && clientType !== 'default' ? `?client_type=${encodeURIComponent(clientType)}` : '';
                        window.location.href = `/simfix${queryParams}`;
                      }
                    }}
                  >
                    <Tooltip
                      direction="top"
                      offset={[0, -10]}
                      opacity={0.95}
                      className="simfix-zone-tooltip"
                    >
                      <div style={{ textAlign: 'center', padding: '4px' }}>
                        <strong>{simfixAreaData.name || 'SIMFIX Area'}</strong>
                        <br />
                        <span style={{ fontSize: '11px', color: '#666' }}>
                          Click to open SIMFIX module
                        </span>
                      </div>
                    </Tooltip>
                  </Circle>
                )}
              </>
            )}

            {/* TL Green Wave - Polygon and Traffic Lights */}
            {showTlGreenWave && (
              <>
                {/* Map click handler for manual point addition */}
                <TlgwMapEventHandler
                  isDrawing={false}
                  isAddingManual={tlgwIsAddingManual}
                  onMapClick={handleTlgwMapClick}
                  onMapDoubleClick={() => { }}
                />

                {/* Feature group to store drawn polygon */}
                <FeatureGroup ref={tlgwFeatureGroupRef} />

                {/* Traffic light markers */}
                {tlgwTrafficLights.map((light) => {
                  const selectionIndex = tlgwSelectedLights.indexOf(light.id);
                  const isSelected = selectionIndex !== -1;

                  // Check if animation is active for this light
                  const animationState = tlgwAnimationStates?.[light.id];
                  const isAnimating = animationState !== undefined;

                  // Determine fill color based on synchronization mode or animation state
                  let fillColor;
                  if (isAnimating) {
                    if (animationState === 'green') fillColor = '#A1E430';
                    else if (animationState === 'yellow') fillColor = '#FFD700';
                    else fillColor = '#ff4d4f'; // red
                  } else if (isSelected) {
                    fillColor = '#3B82F6';
                  } else {
                    // Legend: Green (Synchro), Red (Manual), Yellow (Standby)
                    if (light.tags?.sync) fillColor = '#A1E430';
                    else if (light.tags?.manual) fillColor = '#ff4d4f';
                    else fillColor = '#FFD700'; // Standby
                  }

                  return (
                    <CircleMarker
                      key={light.id}
                      center={[light.lat, light.lon]}
                      radius={isSelected ? 14 : 8}
                      pathOptions={{
                        fillColor: fillColor,
                        fillOpacity: 0.9,
                        color: isSelected ? '#fff' : '#fff',
                        weight: isSelected ? 3 : 2
                      }}
                      eventHandlers={{
                        click: () => handleTlgwLightClick(light.id)
                      }}
                    >
                      <Tooltip direction="top" offset={[0, -5]}>
                        <div style={{ textAlign: 'center' }}>
                          <strong>{light.tags?.sync ? 'Synchro' : light.tags?.manual ? 'Manual' : 'Standby'}</strong>
                          <br />
                          <span style={{ fontSize: '11px' }}>ID: {light.id}</span>
                          {isSelected && (
                            <>
                              <br />
                              <span style={{ fontSize: '11px', color: '#3B82F6' }}>Ordine: {selectionIndex + 1}</span>
                            </>
                          )}
                        </div>
                      </Tooltip>
                    </CircleMarker>
                  );
                })}

                {/* Number labels for selected traffic lights */}
                {tlgwSelectedLights.map((lightId, index) => {
                  const light = tlgwTrafficLights.find(l => l.id === lightId);
                  if (!light) return null;

                  return (
                    <CircleMarker
                      key={`label-${lightId}`}
                      center={[light.lat, light.lon]}
                      radius={0}
                      interactive={false}
                      pathOptions={{ opacity: 0, fillOpacity: 0, interactive: false }}
                    >
                      <Tooltip
                        permanent
                        interactive={false}
                        direction="center"
                        className="tlgw-number-label"
                      >
                        <span className="tlgw-number-text">{index + 1}</span>
                      </Tooltip>
                    </CircleMarker>
                  );
                })}
              </>
            )}

            {/* FIXED: Add controls as children of MapContainer */}
            <MapControls />
          </MapContainer>
        </>
      ) : null}

      {/* Traffic Chart Popup */}
      {showTrafficChart && trafficChartData && (
        <>
          <div className="traffic-chart-overlay" onClick={() => setShowTrafficChart(false)} />
          <TrafficChartPopup
            type={trafficChartData.type}
            id={trafficChartData.id}
            stradeData={stradeData}
            varchiData={varchiData}
            onClose={() => setShowTrafficChart(false)}
          />
        </>
      )}

      {/* TL Green Wave Controls */}
      <TlGreenWaveControls
        isActive={showTlGreenWave}
        isDrawing={tlgwIsDrawing}
        setIsDrawing={setTlgwIsDrawing}
        polygonPoints={tlgwPolygonPoints}
        trafficLights={tlgwTrafficLights}
        isAddingManualPoint={tlgwIsAddingManual}
        setIsAddingManualPoint={setTlgwIsAddingManual}
        onLoadTrafficLights={handleLoadTrafficLights}
        onClearAll={handleClearTlgw}
        isLoadingCarica={tlgwIsLoadingCarica}
        isSelecting={tlgwIsSelecting}
        setIsSelecting={setTlgwIsSelecting}
        selectedLights={tlgwSelectedLights}
        onRemoveLight={handleRemoveTlgwLight}
        onClearSelection={handleClearTlgwSelection}
        onStartDraw={handleStartPolygonDraw}
        onTrafficLightStateChange={handleTlgwAnimationStateChange}
        persistedSemaforiConfig={tlgwPersistedSemaforiConfig}
        setPersistedSemaforiConfig={setTlgwPersistedSemaforiConfig}
        persistedLinksConfig={tlgwPersistedLinksConfig}
        setPersistedLinksConfig={setTlgwPersistedLinksConfig}
        persistedVelocity={tlgwPersistedVelocity}
        setPersistedVelocity={setTlgwPersistedVelocity}
        openConfigModalRequest={tlgwOpenConfigModalRequest}
        onConfigModalRequestConsumed={() => setTlgwOpenConfigModalRequest(null)}
        isEditing={tlgwIsEditing}
        onStartEdit={handleStartPolygonEdit}
      />
    </div>
  );
}

export default MapComponent;
